<template>
    <PageWrapper
        title="Email Notifications"
        :description="description"
    >
        <div class="EmailNotificationSettings__list">
            <EmailTemplateItem
                v-if="producerFlowEnabled"
                :title="newEntityTitle"
                :description="emailTemplateDescription.newEntity"
                :init-enabled="notificationConfigurations.NEW_ENTITY_BODY.initEnabled"
                :reminders-enabled="notificationConfigurations.NEW_ENTITY_BODY.remindersEnabled"
                :schedule-disabled="!newEntityCadenceAvailable"
                @set-reminders="openReminders(NotificationName.newEntityBodyRequestReminder)"
                @edit="editTemplate(NotificationType.newEntity)"
                @send-test-email="sendTestEmail(VerificationRequestType.newEntity)"
            />
            <EmailTemplateItem
                :title="newRequirementsTitle"
                :description="emailTemplateDescription.new"
                :init-enabled="notificationConfigurations.NEW.initEnabled"
                :reminders-enabled="notificationConfigurations.NEW.remindersEnabled"
                @set-reminders="openReminders(NotificationName.newRequestReminder)"
                @edit="editTemplate(NotificationType.new)"
                @send-test-email="sendTestEmail(VerificationRequestType.new)"
            />
            <EmailTemplateItem
                :title="expiringRequirementsTitle"
                :description="emailTemplateDescription.expiring"
                :init-enabled="notificationConfigurations.EXPIRING.initEnabled"
                :reminders-enabled="notificationConfigurations.EXPIRING.remindersEnabled"
                @set-reminders="openReminders(NotificationName.expiringRequestReminder)"
                @edit="editTemplate(NotificationType.expiring)"
                @send-test-email="sendTestEmail(VerificationRequestType.expiring)"
            />
            <EmailTemplateItem
                :title="nonCompliantRequirementsTitle"
                :description="emailTemplateDescription.nonCompliant"
                :init-enabled="notificationConfigurations.NON_COMPLIANT.initEnabled"
                :reminders-enabled="notificationConfigurations.NON_COMPLIANT.remindersEnabled"
                @set-reminders="openReminders(NotificationName.nonCompliantRequestReminder)"
                @edit="editTemplate(NotificationType.nonCompliant)"
                @send-test-email="sendTestEmail(VerificationRequestType.nonCompliant)"
            />
            <EmailTemplateItem
                :title="compliantStatusTitle"
                :description="emailTemplateDescription.compliant"
                :init-enabled="notificationConfigurations.COMPLIANT.initEnabled"
                :reminders-supported="remindersSupported(NotificationType.compliant)"
                @set-reminders="openEnablement(NotificationType.compliant)"
                @edit="editTemplate(NotificationType.compliant)"
                @send-test-email="sendTestEmail(VerificationRequestType.compliant)"
            />
        </div>
    </PageWrapper>
</template>

<style lang="scss">
    .EmailNotificationSettings {
        .Alert {
            justify-content: start;
            border-radius: 6px;
            font-weight: 500;
            font-size: 0.95em;
            padding: 15px 20px;
        }
    }
</style>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { NotificationName } from '@evidentid/config-api-client/types';
    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';
    import { NotificationName as GatewayNotificationName } from '@evidentid/gateway-api-client/src/models';
    import {
        isReminderlessNotificationType,
        NotificationConfiguration,
        NotificationType,
        VerificationRequestType,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    import EmailTemplateItem from '../components/EmailTemplateItem.vue';
    import { getNotificationTypeLabel } from '../utils/getNotificationTypeLabel';

    @Component({
        components: {
            EmailTemplateItem,
            Alert,
            PageWrapper,
        },
    })
    export default class EmailNotificationSettings extends Vue {
        private get notificationsConfigurationStore() {
            return useNotificationsConfigurationStore();
        }

        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private NotificationName = NotificationName;
        private VerificationRequestType = VerificationRequestType;
        private NotificationType = NotificationType;
        private description = 'Control when and how emails are sent. Set notification preferences, schedules, and customize messaging.';
        private emailTemplateDescription = {
            new: 'Automated email to an entity for new requirements.',
            expiring: 'Automated email to an entity with an expiring requirement.',
            nonCompliant: 'Automated email to an entity with a non-compliant status informing of criteria that needs to be addressed.',
            newEntity: 'Automated email to an entity with no active insurance producers, asking them to provide their producers.',
            compliant: 'Automated email to an entity confirming they are now compliant with your requirements.',
        };
        private newEntityCadenceAvailable = true;

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
            if (this.producerFlowEnabled) {
                this.loadNewEntityCadenceAvailability();
            }
            this.loadEmailTemplates();
        }

        private async loadNewEntityCadenceAvailability(): Promise<void> {
            this.newEntityCadenceAvailable = await this.notificationsConfigurationStore
                .checkNotificationCadenceAvailability({
                    rpName: this.rpName,
                    type: GatewayNotificationName.newEntityBodyRequestReminder,
                });
        }

        private async loadEmailTemplates(): Promise<void> {
            await this.notificationsConfigurationStore.loadEmailTemplates({ rpName: this.rpName });
        }

        private get rpName(): string {
            return this.$route.params.rpId as string;
        }

        private get producerFlowEnabled(): boolean {
            return (this as any).$rp.currentDetails?.producerFlowEnabled || false;
        }

        private get newEntityTitle(): string {
            return getNotificationTypeLabel(NotificationType.newEntity);
        }

        private get compliantStatusTitle(): string {
            return getNotificationTypeLabel(NotificationType.compliant);
        }

        private get newRequirementsTitle(): string {
            return getNotificationTypeLabel(NotificationType.new);
        }

        private get expiringRequirementsTitle(): string {
            return getNotificationTypeLabel(NotificationType.expiring);
        }

        private get nonCompliantRequirementsTitle(): string {
            return getNotificationTypeLabel(NotificationType.nonCompliant);
        }

        private get notificationConfigurations(): Record<
            NotificationType,
            { initEnabled?: boolean, remindersEnabled?: boolean }
        > {
            const emailTemplates: Partial<Record<NotificationType, NotificationConfiguration>> =
                this.notificationsConfigurationStore.emailTemplates[this.rpName] || {};
            return {
                [NotificationType.new]: {
                    initEnabled: emailTemplates[NotificationType.new]?.initEnabled,
                    remindersEnabled: emailTemplates[NotificationType.new]?.remindersEnabled,
                },
                [NotificationType.expiring]: {
                    initEnabled: emailTemplates[NotificationType.expiring]?.initEnabled,
                    remindersEnabled: emailTemplates[NotificationType.expiring]?.remindersEnabled,
                },
                [NotificationType.nonCompliant]: {
                    initEnabled: emailTemplates[NotificationType.nonCompliant]?.initEnabled,
                    remindersEnabled: emailTemplates[NotificationType.nonCompliant]?.remindersEnabled,
                },
                [NotificationType.newEntity]: {
                    initEnabled: emailTemplates[NotificationType.newEntity]?.initEnabled,
                    remindersEnabled: emailTemplates[NotificationType.newEntity]?.remindersEnabled,
                },
                [NotificationType.compliant]: {
                    initEnabled: emailTemplates[NotificationType.compliant]?.initEnabled,
                    remindersEnabled: emailTemplates[NotificationType.compliant]?.remindersEnabled,
                },
            };
        }

        @Watch('rpName', { immediate: true })
        private async onRpChange(newVal: string, oldVal: string): Promise<void> {
            if (newVal && newVal !== oldVal) {
                await useRbacStore().validateRbac([ RbacFeature.displayAndEmailSettings ]);
            }
        }

        @Watch('rpName')
        private onRpNameChange(newVal: string, oldVal: string): void {
            if (newVal && newVal !== oldVal) {
                this.loadEmailTemplates();
            }
        }

        private async openReminders(type: NotificationName): Promise<void> {
            await this.$procedures.execute('setNotificationReminders', {
                rpName: this.rpName,
                type,
            });
        }

        private remindersSupported(type: NotificationType): boolean {
            return !isReminderlessNotificationType(type);
        }

        private async openEnablement(type: NotificationType): Promise<void> {
            await this.$procedures.execute('setNotificationEnablement', {
                rpName: this.rpName,
                type,
            });
        }

        private async editTemplate(type: NotificationType): Promise<void> {
            await this.$procedures.execute('editNotificationEmailTemplate', {
                rpName: this.rpName,
                type,
            });
        }

        private async sendTestEmail(type: VerificationRequestType): Promise<void> {
            await this.$procedures.execute('sendTestEmail', {
                rpName: this.rpName,
                type,
            });
        }
    }
</script>
